/** @format */
import {
    ChoiceChip,
    ChoiceChipVariant,
} from '@mpb-com/feather/components/atoms/choice-chip';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { createClassNames } from '@mpb-com/feather/helpers/styles';
import { Trans, useTranslation } from 'next-i18next';
import React, { FunctionComponent, useContext } from 'react';
import styled from 'styled-components';

import ConditionIcon from '@toucan/client/global/icons/condition.svg';

import { MarketContext } from '@toucan/client/global/market/context';

import { Link } from '@toucan/client/shared/components/link';
import { useFeatureFlaggedConfig } from '@toucan/client/shared/hooks/use-feature-flagged-config';

import { FilterOption } from '@toucan/client/features/search/config/types';

import { styles } from './condition-filter.styles';
import {
    intercomProductConditionHelpUrls,
    intercomProductConditionHelpUrlsConsolidatedHC,
} from './config';
import { TEST_ID as dataTestId } from './constants';
import { ConditionFilterProps } from './types';

const StyledConditionFilter = styled('div')`
    ${styles}
`;

export const ConditionFilter: FunctionComponent<ConditionFilterProps> = ({
    filter,
    onConditionSelect,
    showTitle = true,
}) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);
    const { selectedOptions } = filter;

    const productionConditionUrls = useFeatureFlaggedConfig(
        'APE-3584-(consolidate-intercom-workspaces)',
        intercomProductConditionHelpUrls,
        intercomProductConditionHelpUrlsConsolidatedHC
    );

    function handleConditionSelect(
        label: FilterOption['label'],
        value: FilterOption['value'],
        checked: boolean
    ) {
        if (!checked) {
            onConditionSelect(
                selectedOptions.filter((option) => option.value !== value)
            );
            return;
        }

        if (isSelected(value)) {
            onConditionSelect(selectedOptions);
            return;
        }

        onConditionSelect([...selectedOptions, { label, value }]);
    }

    const isSelected = (option: FilterOption['value']) => {
        return selectedOptions.some((selectedOption) => {
            return selectedOption.value === option;
        });
    };

    return (
        <StyledConditionFilter data-testid={dataTestId}>
            <div
                className={createClassNames('condition-filter__title', {
                    'condition-filter__title--hidden': !showTitle,
                })}
            >
                <Icon
                    variant={IconVariant.CORE}
                    size={IconSize.XL}
                    className="condition-filter__condition-icon"
                >
                    <ConditionIcon />
                </Icon>
                <p className="condition-filter__label">{filter.label}</p>
            </div>

            <p className="condition-filter__description">
                <Trans
                    components={{
                        ['learn-more-link']: (
                            <Link
                                target="_blank"
                                aria-label={t('Learn more about conditions')}
                                href={
                                    productionConditionUrls[activeMarket.code]
                                }
                            />
                        ),
                    }}
                >
                    {t(
                        'Our specialists check each item’s cosmetic condition (like new, excellent, good, well used or heavily used). You’ll only see the filter options that apply to your chosen model. <learn-more-link>Learn more about conditions</learn-more-link>'
                    )}
                </Trans>
            </p>

            <div className="condition-filter__options">
                {filter.options.map((option) => (
                    <ChoiceChip
                        key={option.value}
                        variant={ChoiceChipVariant.CHECKBOX}
                        name={option.label}
                        value={option.value.toString()}
                        checked={isSelected(option.value)}
                        // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleConditionSelect(
                                option.label,
                                option.value,
                                e.target.checked
                            )
                        }
                    >
                        {option.label}
                    </ChoiceChip>
                ))}
            </div>
        </StyledConditionFilter>
    );
};
